import { Requirements_AdminDetail, Requirements_Get } from "data/endpoints";
import IDataService from "data/IDataService";
import IRepository from "data/IRepository";
import { getRouteParams } from "features/shared/helpers/routeParams";
import { isEmpty } from "features/shared/helpers/objects";
import { useInjection } from "infrastructure/ioc/ioc.react";
import { IocTypes } from "infrastructure/ioc/ioc.types";
import IAppState from "infrastructure/store/IAppState";
import CustomerState from "infrastructure/store/customer/CustomerState";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import RequirementFullDTO from "../dtos/RequirementFullDTO";
import { ProcessingMode } from "features/shared/enums/processingMode";
import TmRequirementDetailSection from "./TmRequirementDetailSection";
import EmailRequirementDetailSection from "./EmailRequirementDetailSection";
import CrmRequirementDetailSection from "./CrmRequirementDetailSection";
import RequirementAdminActions from "features/requirements/components/RequirementAdminActions";

interface Props {
  requirementId: string;
  forAdminUse?: boolean;
}

const RequirementDetailSection = ({ requirementId, forAdminUse = false }: Props) => {
  // DI
  const dataService = useInjection<IDataService>(IocTypes.DataService);
  const repository = useInjection<IRepository<RequirementFullDTO>>(IocTypes.Repository);

  // store
  const customer = useSelector<IAppState, CustomerState>(
    (store) => store.customer
  ).customer;

  // state
  const [detail, setDetail] = useState<RequirementFullDTO>({});
  const [reload, setReload] = useState(0);

  // side effects / fetch data
  useEffect(() => {
    if (!forAdminUse && !customer?.customerId) {
      return;
    }

    dataService.fetchAndSetOne<RequirementFullDTO>(
      repository,
      setDetail,
      forAdminUse ? Requirements_AdminDetail : Requirements_Get,
      getRouteParams("requirementId", requirementId),
      forAdminUse ? undefined : new URLSearchParams({ customerId: customer!.customerId! })
    );
  }, [requirementId, forAdminUse, customer?.customerId, reload]);

  // sub-renders
  const renderAdminActions = () => {
    if (!forAdminUse) {
      return null;
    }

    return (
      <RequirementAdminActions
        requirementId={requirementId}
        emailRequirementState={detail.emailRequirementState}
        onStateChanged={() => setReload((k) => k + 1)}
      />
    );
  };

  // render
  if (isEmpty(detail)) {
    return null;
  }

  switch (detail.processingMode) {
    case ProcessingMode.TicketManagement:
      return <TmRequirementDetailSection detail={detail} setDetail={setDetail} />;

    case ProcessingMode.CrmApi:
      return <CrmRequirementDetailSection detail={detail} />;

    case ProcessingMode.Email:
      return (
        <>
          {renderAdminActions()}
          <EmailRequirementDetailSection detail={detail} />
        </>
      );

    default:
      return null;
  }
};

export default RequirementDetailSection;
